import config from "../../config";
import React, { useEffect, useState, useMemo, Fragment } from "react";
import styled from "styled-components";
import { IconButton } from "@mui/material";
import theme from "../../styles/theme";
import { sideFadeMask } from "../../styles/variables";
import {
  getDivisionColor,
  getDivisionTextColor,
  getDivisionPriority,
} from "../../utils/helpers/divisionUtils";

import { TitleContainer } from "./LastRaceResults";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { useStandingsContext } from "../../contexts/standings";

import ConstructorStandings from "../ConstructorStandings";
import ConstructorsPodium from "./PodiumConstructors";
import { useScheduleContext } from "../../contexts/schedule";

import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import { TitleTopContainer } from "./LastRaceResults";

import teamChampionSeason1ImagePhone from "../../assets/PodiumBackground/Teams/TeamChampionSeason1Phone.png";
import teamChampionSeason2ImagePhone from "../../assets/PodiumBackground/Teams/TeamChampionSeason2Phone.png";
import teamChampionSeason3ImagePhone from "../../assets/PodiumBackground/Teams/TeamChampionSeason3Phone.png";
import teamChampionSeason4ImagePhone from "../../assets/PodiumBackground/Teams/TeamChampionSeason4Phone.png";
import teamChampionSeason5ImagePhone from "../../assets/PodiumBackground/Teams/TeamChampionSeason5Phone.png";
import teamChampionSeason6ImagePhone from "../../assets/PodiumBackground/Teams/TeamChampionSeason6Phone.png";
import teamChampionSeason7ImagePhone from "../../assets/PodiumBackground/Teams/TeamChampionSeason7Phone.png";
import teamChampionSeasonNonePhone from "../../assets/PodiumBackground/Teams/TeamChampionSeasonNonePhone.png";

import teamChampionSeason7Image from "../../assets/PodiumBackground/Teams/TeamChampionSeason7.png";
import teamChampionSeason6Image from "../../assets/PodiumBackground/Teams/TeamChampionSeason6.png";
import teamChampionSeason5Image from "../../assets/PodiumBackground/Teams/TeamChampionSeason5.png";
import teamChampionSeason4Image from "../../assets/PodiumBackground/Teams/TeamChampionSeason4.png";
import teamChampionSeason3Image from "../../assets/PodiumBackground/Teams/TeamChampionSeason3.png";
import teamChampionSeason2Image from "../../assets/PodiumBackground/Teams/TeamChampionSeason2.png";
import teamChampionSeason1Image from "../../assets/PodiumBackground/Teams/TeamChampionSeason1.png";
import teamChampionSeasonNone from "../../assets/PodiumBackground/Teams/TeamChampionSeasonNone.png";

const SwitcherContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 24px;
  margin-bottom: 16px;
`;

const SwitcherBadge = styled.button`
  background-color: ${(props) =>
    props.$active ? props.$color || "transparent" : "rgba(255, 255, 255, 0.05)"};
  color: ${(props) =>
    props.$active
      ? props.$color
        ? getDivisionTextColor(props.$color)
        : theme.colors.white
      : theme.colors.grey};
  border: 1px solid ${(props) => (props.$active ? "transparent" : "rgba(255, 255, 255, 0.1)")};
  padding: 4px 12px;
  font-size: 16px;
  font-weight: bold;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: "Roboto Condensed", sans-serif;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    background-color: ${(props) =>
      props.$active ? props.$color || "transparent" : "rgba(255, 255, 255, 0.15)"};
  }
`;

const ChangeSeasonBtnContainer = styled.div`
  z-index: 1;
  display: flex;
  justify-content: center;
  gap: 10px;
`;

const StyledIconPrev = styled.i`
  &.swiper-button-disabled {
    background-color: transparent;
    opacity: 0;
    pointer-events: none;
    cursor: default;
  }
`;

const StyledIconNext = styled.i`
  &.swiper-button-disabled {
    background-color: transparent;
    opacity: 0;
    pointer-events: none;
    cursor: default;
  }
`;

const Background = styled.div`
  position: absolute;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  left: 0px;
  height: 300px;
  // background: linear-gradient(
  //   90deg,
  //   ${theme.colors.darkLight} 0%,
  //   ${theme.colors.darkLight} 45%,
  //   ${theme.colors.darkLight} 55%,
  //   ${theme.colors.darkLight} 100%
  // );
`;

const StyledImg = styled.img`
  position: absolute;
  width: 100vw;
  margin-left: calc(50% - 50vw);
  left: 0px;
  height: 300px;
  object-fit: cover;
  opacity: 0.5;
  mask-image: ${sideFadeMask};
  -webkit-mask-image: ${sideFadeMask};
`;

const StyledSwiperSlide = styled(SwiperSlide)`
  height: 300px;
`;

const StyledTitleContainer = styled(TitleContainer)`
  max-width: 800px;
  width: auto;
`;

const BackgroundPodiumTeam = ({ currentSeasonId, setCurrentSeasonId }) => {
  const { loadSeason: loadSeasonStandings, standings } = useStandingsContext();
  const { seasons, loadSchedule } = useScheduleContext({});
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [swiper, setSwiper] = useState(null);

  const currentSeason = seasons[currentSeasonId];
  const divisionColors = currentSeason?.divisionColors;
  const teamsMap = standings[currentSeasonId]?.teams;

  const resolvedDivisions = useMemo(() => {
    if (currentSeason?.childSeasons && currentSeason.childSeasons.length > 0) {
      const list = [];
      currentSeason.childSeasons.forEach((child) => {
        // Prefer fully-loaded season for name/colors, fallback to embedded child data
        const childSeason = seasons[child.id] || child;
        const childDivColors =
          childSeason.divisionColors || child.divisionColors || divisionColors || {};
        const childTeamsMap = standings[child.id]?.teams;
        if (childTeamsMap) {
          const keys = Array.isArray(childTeamsMap) ? [] : Object.keys(childTeamsMap);
          keys.forEach((div) => {
            const label = div === config.defaultDivision ? childSeason.shortName : div;
            list.push({
              key: `${child.id}_${div}`,
              label: label,
              divisionName: div,
              seasonId: child.id,
              colorKey: div,
              divisionColors: childDivColors,
            });
          });
        }
      });
      return list.sort((a, b) => {
        const indexA = currentSeason.childSeasons.findIndex((c) => c.id === a.seasonId);
        const indexB = currentSeason.childSeasons.findIndex((c) => c.id === b.seasonId);
        if (indexA !== indexB) {
          return indexA - indexB;
        }
        return (
          getDivisionPriority(a.divisionColors, a.colorKey) -
          getDivisionPriority(b.divisionColors, b.colorKey)
        );
      });
    }

    if (!teamsMap) return [];
    const keys = Array.isArray(teamsMap) ? [] : Object.keys(teamsMap);
    return keys
      .map((div) => ({
        key: div,
        label: div,
        divisionName: div,
        seasonId: currentSeasonId,
        colorKey: div,
        divisionColors: divisionColors,
      }))
      .sort(
        (a, b) =>
          getDivisionPriority(divisionColors, a.key) - getDivisionPriority(divisionColors, b.key),
      );
  }, [currentSeason, standings, teamsMap, divisionColors, currentSeasonId, seasons]);

  const activeDivisionObj = useMemo(() => {
    return resolvedDivisions.find((d) => d.key === selectedDivision) || resolvedDivisions[0];
  }, [resolvedDivisions, selectedDivision]);

  useEffect(() => {
    if (resolvedDivisions.length > 0) {
      const keys = resolvedDivisions.map((d) => d.key);
      if (!selectedDivision || !keys.includes(selectedDivision)) {
        setSelectedDivision(resolvedDivisions[0].key);
      }
    } else {
      setSelectedDivision(null);
    }
  }, [resolvedDivisions, selectedDivision]);

  const podiumTeams = useMemo(() => {
    if (!activeDivisionObj) return undefined;
    const activeStandings = standings[activeDivisionObj.seasonId]?.teams;
    if (!activeStandings) return undefined;
    if (Array.isArray(activeStandings)) return activeStandings;
    return activeStandings[activeDivisionObj.divisionName] || [];
  }, [standings, activeDivisionObj]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getImage = (season) => {
    switch (season) {
      case 1:
        return windowWidth < 768 ? teamChampionSeason1ImagePhone : teamChampionSeason1Image;
      case 2:
        return windowWidth < 768 ? teamChampionSeason2ImagePhone : teamChampionSeason2Image;
      case 3:
        return windowWidth < 768 ? teamChampionSeason3ImagePhone : teamChampionSeason3Image;
      case 4:
        return windowWidth < 768 ? teamChampionSeason4ImagePhone : teamChampionSeason4Image;
      case 5:
        return windowWidth < 768 ? teamChampionSeason5ImagePhone : teamChampionSeason5Image;
      case 6:
        return windowWidth < 768 ? teamChampionSeason6ImagePhone : teamChampionSeason6Image;
      case 7:
        return windowWidth < 768 ? teamChampionSeason7ImagePhone : teamChampionSeason7Image;
      case 8:
        return windowWidth < 768 ? teamChampionSeasonNonePhone : teamChampionSeasonNone;
      default:
        return windowWidth < 768 ? teamChampionSeasonNonePhone : teamChampionSeasonNone;
    }
  };

  const seasonsList = useMemo(() => {
    return Object.values(seasons)
      .filter((s) => s.simulator && !s.parentSeasonId)
      .sort((a, b) => new Date(b.endDate) - new Date(a.endDate))
      .map((s) => s.id);
  }, [seasons]);

  const prevSeasonId = useMemo(() => {
    const href = currentSeason?._links?.prev?.href;
    if (!href) return null;
    const m = href.match(/\/seasons\/(\d+)/);
    return m ? Number(m[1]) : null;
  }, [currentSeason]);

  const nextSeasonId = useMemo(() => {
    const href = currentSeason?._links?.next?.href;
    if (!href) return null;
    const m = href.match(/\/seasons\/(\d+)/);
    return m ? Number(m[1]) : null;
  }, [currentSeason]);

  const handleSeasonChangeLeft = () => {
    if (prevSeasonId) setCurrentSeasonId(prevSeasonId);
  };

  const handleSeasonChangeRight = () => {
    if (nextSeasonId) setCurrentSeasonId(nextSeasonId);
  };

  useEffect(() => {
    loadSchedule(currentSeasonId, true);
    loadSeasonStandings(currentSeasonId);
  }, [currentSeasonId]); // eslint-disable-line

  useEffect(() => {
    if (!swiper) return;
    const index = seasonsList.indexOf(currentSeasonId);
    if (index >= 0) {
      swiper.slideTo(index);
    }
  }, [swiper, currentSeasonId, seasonsList]);

  const renderPodium = currentSeasonId < 8; //HARDCODE

  return (
    <>
      <ConstructorsPodium teams={podiumTeams} renderPodium={renderPodium}>
        <TitleTopContainer>Командний залік</TitleTopContainer>
        <Fragment>
          <Background>
            <Swiper
              modules={[Pagination]}
              style={{ width: "100%" }}
              watchSlidesProgress={false}
              slidesPerView={"auto"}
              spaceBetween={0}
              className="mySwiper"
              speed={500}
              onSwiper={setSwiper}
              initialSlide={Math.max(0, seasonsList.indexOf(currentSeasonId))}
              noSwiping={true}
              noSwipingSelector=".swiper-slide"
            >
              {seasonsList.map((season) => (
                <StyledSwiperSlide key={season}>
                  <StyledImg src={getImage(season)} />
                </StyledSwiperSlide>
              ))}
            </Swiper>
          </Background>
        </Fragment>
        <ChangeSeasonBtnContainer>
          {prevSeasonId ? (
            <StyledIconPrev onClick={handleSeasonChangeLeft}>
              <IconButton>
                <KeyboardArrowLeftIcon sx={{ fontSize: "60px", color: `${theme.colors.green}` }} />
              </IconButton>
            </StyledIconPrev>
          ) : (
            <div style={{ width: 62 }} />
          )}
          <StyledTitleContainer>{seasons[currentSeasonId]?.shortName}</StyledTitleContainer>
          {nextSeasonId ? (
            <StyledIconNext onClick={handleSeasonChangeRight}>
              <IconButton>
                <KeyboardArrowRightIcon sx={{ fontSize: "60px", color: `${theme.colors.green}` }} />
              </IconButton>
            </StyledIconNext>
          ) : (
            <div style={{ width: 62 }} />
          )}
        </ChangeSeasonBtnContainer>
      </ConstructorsPodium>
      {resolvedDivisions.length > 1 && (
        <SwitcherContainer>
          {resolvedDivisions.map((division) => {
            const divisionColor = getDivisionColor(division.divisionColors, division.colorKey);
            const active = selectedDivision === division.key;
            return (
              <SwitcherBadge
                key={division.key}
                $active={active}
                $color={divisionColor}
                onClick={() => setSelectedDivision(division.key)}
              >
                {division.label}
              </SwitcherBadge>
            );
          })}
        </SwitcherContainer>
      )}
      <div style={{ marginTop: 12 }}>
        <ConstructorStandings
          teams={
            activeDivisionObj
              ? standings[activeDivisionObj.seasonId]?.teams
              : standings[currentSeasonId]?.teams
          }
          selectedDivision={activeDivisionObj ? activeDivisionObj.divisionName : null}
          divisionColors={
            activeDivisionObj
              ? seasons[activeDivisionObj.seasonId]?.divisionColors ||
                seasons[currentSeasonId]?.divisionColors
              : seasons[currentSeasonId]?.divisionColors
          }
        />
      </div>
    </>
  );
};

export default BackgroundPodiumTeam;
